/**
 * Email Verification Sent Page
 * Shown after user registers to inform them to check their email
 */

import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/common/Layout';
import { Card } from '../components/common/Card';

export const EmailVerificationSentPage: React.FC = () => {
  const { user } = useAuth();
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');

  const handleResendEmail = async () => {
    if (!user) return;

    setResending(true);
    setResendMessage('');
    setResendError('');

    try {
      const resendVerificationEmail = httpsCallable(functions, 'resendVerificationEmail');
      const result = await resendVerificationEmail({});
      const data = result.data as { success: boolean; message: string };

      if (data.success) {
        setResendMessage('Verification email sent! Please check your inbox.');
      } else {
        setResendError(data.message || 'Failed to resend email. Please try again later.');
      }
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      
      let errorMessage = 'An error occurred. Please try again later.';
      
      if (error.code === 'functions/resource-exhausted') {
        errorMessage = error.message || 'Please wait before requesting another email.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setResendError(errorMessage);
    } finally {
      setResending(false);
    }
  };

  return (
    <Layout showFooter={false}>
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          <Card>
            <div className="text-center">
              {/* Email Icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mb-6">
                <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>

              <h1 className="text-3xl font-bold text-gray-800 mb-4">Check Your Email</h1>
              
              <p className="text-gray-600 mb-2">
                We've sent a verification email to:
              </p>
              
              <p className="text-lg font-semibold text-purple-600 mb-6">
                {user?.email}
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What's next?</strong>
                </p>
                <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                  <li>Check your inbox (and spam folder)</li>
                  <li>Click the verification link in the email</li>
                  <li>Start learning!</li>
                </ol>
              </div>

              {/* Resend Email Section */}
              <div className="mb-6">
                {resendMessage && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-green-700">{resendMessage}</p>
                  </div>
                )}
                
                {resendError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-red-700">{resendError}</p>
                  </div>
                )}

                <p className="text-sm text-gray-600 mb-3">
                  Didn't receive the email?
                </p>
                
                <button
                  onClick={handleResendEmail}
                  disabled={resending}
                  className="w-full bg-white border-2 border-purple-600 text-purple-600 py-2 px-6 rounded-lg font-semibold hover:bg-purple-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resending ? 'Sending...' : 'Resend Verification Email'}
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-6">
                Need help? Contact us at{' '}
                <a href="mailto:support@trainedup.ai" className="text-purple-600 hover:underline">
                  support@trainedup.ai
                </a>
              </p>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};
